/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from 'react';
import { ModalProvider } from './src/components/Modals/ModalContext';
import config from './src/config.json';
import translations from './src/translations/main.json';
import {
  resolveRedirectTarget,
  matchShareablePassengerPage,
  matchShareableClaimPage,
  buildPassengerRedirectTarget,
  buildClaimRedirectTarget,
  buildTargetUrl,
  getTargetHost,
} from './src/utils/newCalculatorRedirect';
import {
  getPartner,
  loadEffectiveConfig,
  isCobrandingBuild,
} from './src/utils/partnerConfig';

import './src/assets/styles/globals.css';

/**
 * The shareable pages carry only an auth token (plus, sometimes, a passenger
 * id), but the new calculator needs the CLAIM number in the path. Fetch the
 * claim from the API with the token (same `/claims` endpoint the old shareable
 * pages use), then forward to /app/claim-tasks/<claimNumber>?<token param>=.
 * On any failure we do nothing, leaving the old page usable.
 *
 * `apiUrl` comes from the effective config, so on the shared cobranding build
 * the lookup hits the partner's API rather than the build default's.
 */
const resolveClaimNumberForToken = async (apiUrl, token) => {
  try {
    const response = await fetch(`${apiUrl}/claims`, {
      headers: {
        Accept: 'application/vnd.api+json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      return null;
    }
    const body = await response.json();
    return body?.data?.[0]?.attributes?.claim_number || null;
  } catch (error) {
    return null;
  }
};

/**
 * True once the browser has been handed a cross-domain navigation.
 *
 * `window.location.replace()` does not stop this page: gatsby carries on
 * hydrating and the old app can navigate client-side while the navigation is
 * still in flight. The affiliate page does exactly that — it forwards to
 * /congratulations as soon as it has created the cobranding customer — and the
 * second replace() supersedes the first, dropping the visitor at the generic
 * funnel entry instead of the affiliate deep link. The URL the visitor actually
 * arrived on is the one that should win, so the first redirect is final.
 */
let hasRedirected = false;

/**
 * Forward old-funnel URLs to the new calculator when this site has been
 * migrated. Controlled entirely by the baked site config:
 *   - shouldRedirectToNewCalculator: master on/off switch (per site)
 *   - newCalculatorDomain: destination host for the new funnel/account, which
 *     may carry a path when the new funnel sits under a locale folder
 *     (e.g. "www.flight-delayed.com/en")
 * When the flag is off (every non-migrated site) this is a no-op.
 *
 * On the shared cobranding build those two keys come from the *partner's*
 * config rather than the build default, so the destination is the partner's own
 * main domain (gate1-nl -> claimservice.gate1.nl). With no resolvable partner
 * we deliberately do nothing and keep serving the old page, rather than guess.
 */
const resolveAndRedirect = async (pathname, search) => {
  if (hasRedirected) {
    return;
  }

  const partner = getPartner(search);
  const siteConfig = await loadEffectiveConfig(partner);

  if (
    !siteConfig.shouldRedirectToNewCalculator ||
    !siteConfig.newCalculatorDomain
  ) {
    return;
  }
  // Guard against a redirect loop if we are somehow already on the new domain.
  if (window.location.host === getTargetHost(siteConfig.newCalculatorDomain)) {
    return;
  }

  const goToNewCalculator = target => {
    hasRedirected = true;
    window.location.replace(
      buildTargetUrl(siteConfig.newCalculatorDomain, target)
    );
  };

  const target = resolveRedirectTarget(pathname, translations, search, {
    // Both conditions matter: a stray `?partner=` on an own-brand site must not
    // turn its funnel entry points into /get-compensation deep links.
    isCobranding: isCobrandingBuild && Boolean(partner),
  });
  if (target) {
    // Forward without prefill. The incoming query string is dropped, except
    // where a rule explicitly carries something across: the shareable claim
    // page token (remapped to `?claim_token=`), the cobranding dossierToken
    // (which becomes the /get-compensation/<token> path) and the affiliate
    // page, which keeps the query string as-is.
    goToNewCalculator(target);
    return;
  }

  // Shareable pages entered without a claim number in the path: the claim has
  // to be looked up with the token first (see helper above). Without a token
  // there is nothing to look up, so the old page is left alone.
  const passenger = matchShareablePassengerPage(pathname, translations, search);
  const shareable =
    passenger || matchShareableClaimPage(pathname, translations, search);
  if (!shareable || !shareable.token) {
    return;
  }

  const claimNumber = await resolveClaimNumberForToken(
    siteConfig.apiUrl || config.apiUrl,
    shareable.token
  );
  if (claimNumber) {
    goToNewCalculator(
      passenger
        ? buildPassengerRedirectTarget(claimNumber, shareable.token)
        : buildClaimRedirectTarget(claimNumber, shareable.token)
    );
  }
};

/**
 * Serialise the redirect decisions. Both entry points below resolve their
 * config asynchronously, so without this a client-side navigation could finish
 * deciding *before* the entry URL did and win the race to replace(). Queuing
 * makes the entry URL's decision the first one, and therefore the final one.
 * A failure must not poison the queue, hence the catch.
 */
let pendingRedirect = Promise.resolve();

const redirectToNewCalculator = (pathname, search) => {
  pendingRedirect = pendingRedirect.then(() =>
    resolveAndRedirect(pathname, search).catch(() => {})
  );
  return pendingRedirect;
};

// Initial load (bookmarks, email links).
export const onClientEntry = () => {
  redirectToNewCalculator(window.location.pathname, window.location.search);
};

// Client-side navigations within the old app.
export const onPreRouteUpdate = ({ location }) => {
  redirectToNewCalculator(location.pathname, location.search);
};

// eslint-disable-next-line
export const wrapRootElement = ({ element }) => {
  // Start the mocking conditionally.
  // if (process.env.NODE_ENV === 'development') {
  //   const { worker } = require('./jest-configs/__mocks__/browser')
  //   worker.start({
  //     onUnhandledRequest: 'bypass',
  //   })
  // }

  return <ModalProvider>{element}</ModalProvider>;
};
